import { initializeApp } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence, connectAuthEmulator } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getDatabase, connectDatabaseEmulator } from "firebase/database";
import { getAnalytics, isSupported } from "firebase/analytics";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

// All config values are loaded from environment variables (.env file)
// Never hardcode API keys directly in source code
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);

// ─── App Check ───
// Protects your backend from abuse by verifying requests come from your app.
// Uses reCAPTCHA v3 (invisible, zero-friction for users).
if (window.location.hostname === "localhost") {
    // Enable debug token for local development / emulator use
    // @ts-expect-error — Firebase App Check debug token flag
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true;
}

const appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(
        import.meta.env.VITE_RECAPTCHA_SITE_KEY
    ),
    isTokenAutoRefreshEnabled: true,
});

export { appCheck };

// Initialize Analytics ONLY in production (not localhost)
if (window.location.hostname !== "localhost") {
    isSupported().then((supported) => {
        if (supported) getAnalytics(app);
    });
}

// ─── Auth ───
// Explicitly set browserLocalPersistence: users stay logged in across browser
// restarts until they explicitly log out or clear browser data.
export const auth = getAuth(app);

setPersistence(auth, browserLocalPersistence).catch((err) => {
    console.warn("Failed to set auth persistence:", err);
});

export const db = getFirestore(app);
export const functions = getFunctions(app);
export const storage = getStorage(app);
export const rtdb = getDatabase(app);

// Connect to Firebase Emulators when running locally
if (window.location.hostname === "localhost") {
    connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
    connectFirestoreEmulator(db, "localhost", 8080);
    connectDatabaseEmulator(rtdb, "localhost", 9000);
    connectFunctionsEmulator(functions, "localhost", 5001);
    connectStorageEmulator(storage, "localhost", 9199);
    console.log("🔧 Connected to Firebase Emulators (Firestore:8080, RTDB:9000, Functions:5001, Storage:9199, Auth:9099)");
}

export default app;
